import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Container, Alert } from '@mui/material';

const ContactsPage: React.FC = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (field: 'name' | 'email' | 'message') => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((s) => ({ ...s, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const next: { [k: string]: string } = {};
    if (!form.name.trim()) next.name = 'Jméno je povinné';
    if (!form.email.trim()) next.email = 'Email je povinný';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = 'Zadejte platný email';
    if (!form.message.trim()) next.message = 'Zpráva je povinná';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    try {
      setLoading(true);
      // In this version, we just simulate send and show success info.
      await new Promise((r) => setTimeout(r, 800));
      setSent(true);
      setForm({ name: '', email: '', message: '' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: '100%', background: '#F2F2F2' }}>
      <Container maxWidth={false} sx={{ maxWidth: '1200px', px: { xs: 2, sm: 3, md: 4 }, py: { xs: 6, sm: 8 } }}>
        {/* Heading */}
        <Box sx={{ textAlign: 'center', mb: { xs: 4, sm: 6 } }}>
          <Typography sx={{ fontFamily: 'Inter', fontWeight: 800, fontSize: { xs: '28px', sm: '36px' }, color: '#1F1F1F' }}>
            Kontakty
          </Typography>
          <Typography sx={{ fontFamily: 'Inter', fontWeight: 400, fontSize: { xs: '14px', sm: '16px' }, color: '#666', mt: 1 }}>
            Napište nám – ozveme se co nejdříve.
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: { xs: 3, sm: 4 } }}>
          {/* Contact form */}
          <Box component="form" onSubmit={handleSubmit} sx={{
            p: { xs: 2.5, sm: 3 },
            background: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0px 4px 20px rgba(0,0,0,0.06)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2.5
          }}>
            {sent && (
              <Alert severity="success">Děkujeme za zprávu. Brzy se vám ozveme.</Alert>
            )}

            <TextField
              label="Jméno *"
              value={form.name}
              onChange={handleChange('name')}
              error={!!errors.name}
              helperText={errors.name}
              fullWidth
            />

            <TextField
              label="Email *"
              type="email"
              value={form.email}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              fullWidth
            />

            <TextField
              label="Zpráva *"
              value={form.message}
              onChange={handleChange('message')}
              error={!!errors.message}
              helperText={errors.message}
              fullWidth
              multiline
              rows={5}
            />

            <Button
              type="submit"
              disabled={loading}
              sx={{
                mt: 1,
                padding: '14px 24px',
                bgcolor: '#1F1F1F !important',
                color: '#FFFFFF !important',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                '&:hover': { bgcolor: '#e72f0d !important' }
              }}
            >
              {loading ? 'Odesílám…' : 'Odeslat'}
            </Button>
          </Box>

          {/* Company info */}
          <Box sx={{
            p: { xs: 2.5, sm: 3 },
            background: '#FFFFFF',
            borderRadius: '16px',
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0px 4px 20px rgba(0,0,0,0.06)'
          }}>
            <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '18px', color: '#1F1F1F', mb: 1.5 }}>
              Kontaktní údaje
            </Typography>
            <Typography sx={{ color: '#444', mb: 1 }}>
              Email: <a href="mailto:info@pravnibot.cz">info@pravnibot.cz</a>
            </Typography>
            <Typography sx={{ fontFamily: 'Inter', fontWeight: 700, fontSize: '18px', color: '#1F1F1F', mt: 3, mb: 1 }}>
              Provozovatel
            </Typography>
            <Typography sx={{ color: '#444' }}>Apertia Robotics s.r.o.</Typography>
            <Typography sx={{ color: '#444' }}>Matěje Koštíře 269, Sedlčánky</Typography>
            <Typography sx={{ color: '#444' }}>250 88 Čelákovice</Typography>
            <Typography sx={{ color: '#444', mt: 1 }}>IČO: 22233270</Typography>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ContactsPage;

